import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	inject,
	Input,
	OnInit,
} from '@angular/core';
import {
	AlarisIconDirective,
	AlarisLanguageService,
	AlarisTooltipDirective,
	pluralize,
} from '@campaign-portal/components-library';
import { Package } from '@campaign-portal/namespace/entities/subscriptions/specs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { CountriesNetworksService } from '../../../services/countries-networks.service';

const MAX_VISIBLE_COUNTRIES = 3;
const MAX_VISIBLE_NETWORKS = 6;

@Component({
	selector: 'app-package-country-network-list',
	templateUrl: './package-country-network-list.component.html',
	styleUrl: './package-country-network-list.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslateModule, AlarisTooltipDirective, AlarisIconDirective],
	standalone: true,
	host: {
		'[class.in-order]': 'maxVisibleNetworks === MAX_VISIBLE_NETWORKS',
	},
})
export class PackageCountryNetworkListComponent implements OnInit {
	@Input({ required: true }) package!: Package;

	readonly MAX_VISIBLE_COUNTRIES = MAX_VISIBLE_COUNTRIES;
	readonly MAX_VISIBLE_NETWORKS = MAX_VISIBLE_NETWORKS;
	@Input() maxVisibleNetworks = this.MAX_VISIBLE_NETWORKS;

	readonly countriesNetworks: Map<string, Set<string>> = new Map();
	readonly expandedNetworks: Map<string, boolean> = new Map();
	expandedCountries = false;
	private readonly destroyRef = inject(DestroyRef);

	constructor(
		private readonly countriesNetworksService: CountriesNetworksService,
		private readonly ls: AlarisLanguageService,
		private readonly cd: ChangeDetectorRef
	) {}

	get countries(): string[] {
		const countries = [...this.countriesNetworks.keys()];
		return this.expandedCountries ? countries : countries.slice(0, MAX_VISIBLE_COUNTRIES);
	}

	getNetworks(country: string): string[] {
		const networks = [...(this.countriesNetworks.get(country) ?? [])];
		const expanded = this.expandedNetworks.get(country);
		return expanded ? networks : networks.slice(0, this.maxVisibleNetworks);
	}

	toggleNetworksVisibility(country: string): void {
		const expanded = this.expandedNetworks.get(country);
		this.expandedNetworks.set(country, !expanded);
	}

	toggleCountriesVisibility(): void {
		this.expandedCountries = !this.expandedCountries;
	}

	ngOnInit(): void {
		this.countriesNetworksService.map$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.init();
			this.cd.detectChanges();
		});
	}

	getNetworksPostfix(amount: number): string {
		const text = pluralize(amount, [
			'purchase.networksPostfix.one',
			'purchase.networksPostfix.few',
			'purchase.networksPostfix.other',
		]);
		return ' ' + this.ls.translate(text).trim();
	}

	private init(): void {
		const allNetworksString = this.ls.translate('purchase.allNetworks');

		this.package.availableCountryNetList?.forEach((id) => {
			const countryNetworkRef = this.countriesNetworksService.map.get(id);
			if (!countryNetworkRef) {
				return;
			}
			const { country } = countryNetworkRef;
			let { network } = countryNetworkRef;

			if (network === 'All networks' || !network) {
				network = allNetworksString;
			}

			if (this.countriesNetworks.has(country)) {
				this.countriesNetworks.get(country)?.add(network);
			} else {
				this.countriesNetworks.set(country, new Set([network]));
			}
		});

		for (const [country, networksSet] of this.countriesNetworks.entries()) {
			if (networksSet.has(allNetworksString)) {
				this.countriesNetworks.set(country, new Set([allNetworksString]));
			}
		}
	}
}
